import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Badge, Button, Card, Col, Form, InputGroup, Row, Spinner } from 'react-bootstrap';
import { getJson } from '../utils/shopApi';
import ReportProductModal from './ReportProductModal';

const SORTS = [
  { key: 'featured', label: 'Featured' },
  { key: 'price_asc', label: 'Price: low to high' },
  { key: 'price_desc', label: 'Price: high to low' },
  { key: 'name', label: 'Name' },
];

const rupees = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 });

/**
 * The products page of ibigdo.com/shop: products from the stores that chose to be on it. A card says who sells it and goes to that store's own product page; every
 * purchase is made in the seller's store, with their payment and invoice. Nothing shows until some store has opted in.
 */
export default function ShopDirectory({ onAvailable }) {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') || '';
  const category = params.get('category') || '';
  const state = params.get('state') || '';
  const minPrice = params.get('min') || '';
  const maxPrice = params.get('max') || '';
  const inStock = params.get('stock') === '1';
  const sort = params.get('sort') || 'featured';
  const page = Math.max(0, parseInt(params.get('page') || '0', 10) || 0);

  const [typed, setTyped] = useState(q);
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [reporting, setReporting] = useState(null);

  const go = (changes) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => { if (v === '' || v == null || v === false) next.delete(k); else next.set(k, v === true ? '1' : String(v)); });
    if (!('page' in changes)) next.delete('page');
    setParams(next, { replace: true });
  };

  // The words typed are sent a moment after the last key
  useEffect(() => {
    if (typed === q) return undefined;
    const t = setTimeout(() => go({ q: typed.trim() }), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typed]);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    if (category) p.set('category', category);
    if (state) p.set('state', state);
    if (minPrice) p.set('minPrice', minPrice);
    if (maxPrice) p.set('maxPrice', maxPrice);
    if (inStock) p.set('inStock', 'true');
    p.set('sort', sort);
    p.set('page', String(page));
    return p.toString();
  }, [q, category, state, minPrice, maxPrice, inStock, sort, page]);

  useEffect(() => {
    let live = true;
    getJson(`/api/public/shops/products?${query}`).then((d) => { if (live) { setData(d); setFailed(false); } }).catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [query]);

  const filtered = Boolean(q || category || state || minPrice || maxPrice || inStock);
  // Until a store has chosen to be here there is nothing to browse, and the page stays as it was
  const available = !failed && Boolean(data) && (data.stores > 0 || filtered);

  // The landing page makes room for the products when there are some, and says so when it is known either way
  useEffect(() => {
    if (!onAvailable) return;
    if (failed || data) onAvailable(available);
  }, [onAvailable, available, failed, data]);

  if (!available) return null;

  return (
    <section data-testid="shop-directory">
      <Card className="shop-card p-3 mb-3">
        <Row className="g-2 align-items-end">
          <Col md={4}>
            <InputGroup>
              <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
              <Form.Control value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Search products" aria-label="Search products" data-testid="directory-search" maxLength={100} />
            </InputGroup>
          </Col>
          <Col xs={6} md={2}>
            <Form.Select value={category} onChange={(e) => go({ category: e.target.value })} aria-label="Category" data-testid="directory-category">
              <option value="">All categories</option>
              {data.categories.map((c) => <option key={c.name} value={c.name}>{c.name} ({c.count})</option>)}
            </Form.Select>
          </Col>
          <Col xs={6} md={2}>
            <Form.Select value={state} onChange={(e) => go({ state: e.target.value })} aria-label="State" data-testid="directory-state">
              <option value="">Anywhere in India</option>
              {data.states.map((c) => <option key={c.name} value={c.name}>{c.name} ({c.count})</option>)}
            </Form.Select>
          </Col>
          <Col xs={6} md={2}>
            <InputGroup>
              <Form.Control type="number" min="0" inputMode="decimal" placeholder="Min ₹" aria-label="Lowest price" defaultValue={minPrice} key={`min-${minPrice}`}
                onBlur={(e) => go({ min: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') go({ min: e.currentTarget.value }); }} />
              <Form.Control type="number" min="0" inputMode="decimal" placeholder="Max ₹" aria-label="Highest price" defaultValue={maxPrice} key={`max-${maxPrice}`}
                onBlur={(e) => go({ max: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') go({ max: e.currentTarget.value }); }} />
            </InputGroup>
          </Col>
          <Col xs={6} md={2}>
            <Form.Select value={sort} onChange={(e) => go({ sort: e.target.value === 'featured' ? '' : e.target.value })} aria-label="Sort by" data-testid="directory-sort">
              {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </Form.Select>
          </Col>
          <Col xs={12}>
            <Form.Check type="switch" id="directory-stock" label="In stock only" checked={inStock} onChange={(e) => go({ stock: e.target.checked })} />
          </Col>
        </Row>
      </Card>

      <p className="small text-muted mb-2">
        {data.stores} {data.stores === 1 ? 'store shows' : 'stores show'} their products here. Each sale is made in the seller&apos;s own store, with their own payment, delivery and invoice.
      </p>

      {data.products.length === 0 ? (
        <div className="shop-card text-center text-muted py-5" data-testid="directory-empty">
          <i className="bi bi-bag fs-1 d-block mb-2" />No products match.{' '}
          {filtered && <Button variant="link" className="p-0 align-baseline" onClick={() => { setTyped(''); setParams({}, { replace: true }); }}>Clear the search</Button>}
        </div>
      ) : (
        <>
          <p className="small text-muted mb-2" data-testid="directory-count">{data.total} {data.total === 1 ? 'product' : 'products'}</p>
          <Row className="g-3 g-md-4" data-testid="directory-products">
            {data.products.map((p) => (
              <Col key={`${p.store.slug}-${p.id}`} xs={6} md={4} lg={3}>
                <Card className="product-card h-100">
                  <Link to={`/shop/${p.store.slug}/product/${p.id}`} className="photo" aria-label={p.name}>
                    {p.thumb ? <img src={p.thumb} alt={p.name} loading="lazy" /> : <span className="d-flex align-items-center justify-content-center h-100 text-muted"><i className="bi bi-image fs-1" /></span>}
                    {!p.inStock && <Badge bg="dark">Out of stock</Badge>}
                  </Link>
                  <Card.Body className="d-flex flex-column">
                    <Link to={`/shop/${p.store.slug}/product/${p.id}`} className="name">{p.name}</Link>
                    <div className="mt-auto pt-2">
                      <div className="mb-1">
                        <span className="shop-price">{rupees.format(Number(p.price))}</span>{' '}
                        {Number(p.gstRate) > 0 && <span className="small text-muted">{p.pricesIncludeGst ? `incl. ${Number(p.gstRate)}% GST` : `+ ${Number(p.gstRate)}% GST`}</span>}
                      </div>
                      <div className="small text-muted text-truncate" title={`${p.store.name}${p.store.place ? `, ${p.store.place}` : ''}`}>
                        Sold by <Link to={`/shop/${p.store.slug}`}>{p.store.name}</Link>{p.store.place ? ` · ${p.store.place}` : ''}
                      </div>
                      <Button variant="link" size="sm" className="p-0 text-muted small" onClick={() => setReporting(p)} data-testid="directory-report"><i className="bi bi-flag me-1" />Report</Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
          {data.pages > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
              <Button size="sm" variant="outline-secondary" className="shop-chip" disabled={data.page <= 0} onClick={() => go({ page: String(data.page - 1) })}><i className="bi bi-chevron-left" /> Previous</Button>
              <span className="small text-muted">Page {data.page + 1} of {data.pages}</span>
              <Button size="sm" variant="outline-secondary" className="shop-chip" disabled={data.page >= data.pages - 1} onClick={() => go({ page: String(data.page + 1) })}>Next <i className="bi bi-chevron-right" /></Button>
            </div>
          )}
        </>
      )}
      {!data && <div className="text-center py-5"><Spinner animation="border" /></div>}
      {reporting && <ReportProductModal slug={reporting.store.slug} productId={reporting.id} productName={reporting.name} onClose={() => setReporting(null)} />}
    </section>
  );
}
